import React, {FC, useMemo} from "react";
import {Box, Stack, Theme, Typography, useMediaQuery} from "@mui/material";
import Grid from "@mui/material/Grid";
import {useTranslation} from "react-i18next";
import {getTextColorOnRecommended} from "../../../../../util/utils";

interface Props {
    recommended: boolean;
    countDown: null | number;
    drawStarted: boolean;
    comingSoon?: boolean; // 👈 new prop
}

const TimeUntilStartGrid: FC<Props> = ({countDown, recommended, drawStarted, comingSoon = false}) => {
    const {t} = useTranslation();
    const matchesSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const getColor = useMemo(() => getTextColorOnRecommended(recommended), [recommended]);

    const getTimeUnits = useMemo(() => {
        if (countDown) {
            // Use ceil so UI does not show "0s" before draw actually starts.
            const totalSeconds = Math.max(Math.ceil(countDown / 1000), 0);
            const days = Math.floor(totalSeconds / (60 * 60 * 24));
            const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
            const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
            const seconds = totalSeconds % 60;
            return {days, hours, minutes, seconds};
        }
        return {days: 0, hours: 0, minutes: 0, seconds: 0};
    }, [countDown]);

    const timeUnitList = useMemo(
        () => [
            {label: matchesSm ? `${t('days_short')}.` : t('days'), value: getTimeUnits.days},
            {label: matchesSm ? `${t('hours_short')}.` : t('hours'), value: getTimeUnits.hours},
            {label: matchesSm ? `${t('minutes_short')}.` : t('minutes'), value: getTimeUnits.minutes},
            {label: matchesSm ? `${t('seconds_short')}.` : t('seconds'), value: getTimeUnits.seconds}
        ],
        [getTimeUnits, matchesSm, t]
    );

    if (!countDown || countDown < 0 || drawStarted) return null;

    return (
        <Grid
            container
            justifyContent="space-between"
            flexWrap="nowrap"
            sx={{
                gap: comingSoon ? {xs: 3, md: 4} : 0,
            }}
        >
            {timeUnitList.map((tu, idx) => (
                <Box key={`time-unit-item-${idx}`} textAlign="center">
                    <Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                color: getColor,
                                fontSize: comingSoon
                                    ? {xs: '0.9rem', sm: '1.1rem'}
                                    : '0.75rem', // 👈 larger labels if comingSoon
                            }}
                            fontWeight={comingSoon ? 500 : 400}
                        >
                            {tu.label}
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                color: getColor,
                                fontSize: comingSoon
                                    ? {xs: '2rem', sm: '3rem', md: '3.5rem'}
                                    : '1.5rem', // 👈 bigger numbers
                                lineHeight: 1,
                            }}
                            fontWeight={600}
                        >
                            {tu.value}
                        </Typography>
                    </Stack>
                </Box>
            ))}
        </Grid>
    );
};

export default TimeUntilStartGrid;
